import React, { useEffect, useState } from 'react'
import {
  Form,
  Button,
  Row,
  Col,
  CenteredSpinner,
  Alert,
  FileDropzone,
  Tooltip,
  OverlayTrigger
} from 'react-angle-dashboard-components'
import {
  GetClaimsByMemberReqBody,
  GetClaimsByNumberReqBody
} from '../../services/claims'
import { useMutation } from 'react-query'
import { SmallTitle, FormLabel, FormControl } from './styles'
import { claimsService } from '../../services'
import styled from 'styled-components'
import { PhoneControl } from '../../components/PhoneControl/PhoneControl'

const reconsiderationReasons = [
  'Missing RA / EOB',
  'Check Reconsideration',
  'Claim Adjudication Does Not Match Benefit',
  'Claim Denied No Prior Auth',
  'Incorrect Patient Responsibility',
  'Line Item Request',
  'Requested Medical Records Received',
  'Wrong Address Sent',
  'Other'
]

const disputeReasons = [
  'Claim Denied: Not a Covered Benefit',
  'Claim Rejected: Inconsistent Date Range',
  'Claim Rejected: No Prior Auth',
  'Claim Rejected: Timely Filing',
  'Claim Line Item Rejected',
  'Coding/Bundling',
  'Date of Service Adjustment',
  'Reimbursement/Fee Schedule',
  'Units',
  'Other'
]

const HelpIcon = styled.i`
  color: var(--gray-400);
  height: 10px;
  width: 10px;
  position: relative;
  top: 0px;
  left: 4px;
`

export const TicketForm = React.forwardRef<
  HTMLHeadingElement,
  {
    searchQuery: GetClaimsByMemberReqBody | GetClaimsByNumberReqBody
    isAddressIssue: boolean
    clearResults: (msg: string) => void
  }
>(({ searchQuery, isAddressIssue, clearResults }, ref) => {
  const [responseType, setResponseType] = useState<'email' | 'fax'>('email')

  const [email, setEmail] = useState('')
  const [fax, setFax] = useState('')
  const [phone, setPhone] = useState('')
  const [issueContext, setIssueContext] = useState('')
  const [issueType, setIssueType] = useState<
    'reconsideration' | 'generalInquiry' | 'dispute'
  >('reconsideration')
  const [issueReasonOptions, setIssueReasonOptions] = useState(
    reconsiderationReasons
  )
  const [issueReason, setIssueReason] = useState('')
  const [filesToUpload, setFilesToUpload] = useState<File[] | undefined>()
  const [requestorName, setRequestorName] = useState('')

  const reconsiderationLabel = 'Reconsideration'
  const disputeLabel = 'Dispute/Appeal'
  const generalInquiryLabel = 'General Inquiry'

  const [reasonSelectLabel, setReasonSelectLabel] =
    useState(reconsiderationLabel)

  const [fileDropzoneKey, setFileDropzoneKey] = useState(1)
  const [phoneKey, setPhoneKey] = useState(1)
  const [faxKey, setFaxKey] = useState(1)

  useEffect(() => {
    switch (issueType) {
      case 'reconsideration':
        setReasonSelectLabel(reconsiderationLabel)
        setIssueReasonOptions(reconsiderationReasons)
        setIssueReason(reconsiderationReasons[0])
        break
      case 'dispute':
        setReasonSelectLabel(disputeLabel)
        setIssueReasonOptions(disputeReasons)
        setIssueReason(disputeReasons[0])
        break
      case 'generalInquiry':
        setReasonSelectLabel(generalInquiryLabel)
        setIssueReasonOptions([])
        setIssueReason('')
        break
    }
  }, [issueType])

  useEffect(() => {
    if (isAddressIssue) {
      setIssueType('reconsideration')
      setIssueReason('Wrong Address Sent')
    }
  }, [isAddressIssue])

  const createTicket = useMutation((data: FormData) =>
    claimsService.createTicket(data)
  )

  const clearAllInputs = () => {
    setEmail('')
    setFax('')
    setPhone('')
    setIssueContext('')
    setIssueType('reconsideration')
    setIssueReasonOptions(reconsiderationReasons)
    setIssueReason('')
    setResponseType('email')
    setFilesToUpload(undefined)
    setRequestorName('')
    setFileDropzoneKey((k) => k + 1)
    setPhoneKey((k) => k + 1)
    setFaxKey((k) => k + 1)
    clearResults("Thank you, we'll be in touch soon!")
  }

  const onSubmit = async (e: any) => {
    e.preventDefault()

    const data: any = {}

    if (responseType === 'email') {
      data.contact = {
        email
      }
    } else {
      data.contact = {
        fax
      }
    }

    if (phone) {
      data.contact.phone = phone
    }

    data.criteria = searchQuery

    data.issue = {
      context: issueContext,
      reason: issueReason
    }

    switch (issueType) {
      case 'reconsideration':
        data.issue.type = 'RECONSIDERATION'
        if (issueReason === 'Wrong Address Sent') {
          data.issue.type = 'WRONG ADDRESS SENT'
        }
        break
      case 'dispute':
        data.issue.type = 'DISPUTE/APPEAL'
        break
      case 'generalInquiry':
        data.issue.type = 'GENERAL INQUIRY'
        break
    }

    data.requestor_name = requestorName

    const formData = new FormData()

    formData.append('data', JSON.stringify(data))

    if (filesToUpload) {
      filesToUpload.forEach((f) => {
        formData.append('file', f, f.name)
      })
    }

    try {
      await createTicket.mutateAsync(formData)
      clearAllInputs()
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <Form className="pt-5" onSubmit={onSubmit}>
      {' '}
      <SmallTitle ref={ref}>
        See an issue with a claim? Submit a ticket here.
      </SmallTitle>
      <Row style={{ paddingRight: 15, paddingLeft: 15, marginBottom: 15 }}>
        <Form.Check
          inline
          name="issueType"
          type="radio"
          label={
            <>
              {reconsiderationLabel}
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip
                    data-testid="about-tooltip"
                    id="reconsideration tooltip"
                  >
                    Formal reviews of claims reimbursements or coding decisions,
                    or claims that require reprocessing.
                  </Tooltip>
                }
              >
                <HelpIcon
                  data-testid="about-tooltip-trigger"
                  className="icon icon-question-mark fs-12"
                />
              </OverlayTrigger>
            </>
          }
          checked={issueType === 'reconsideration'}
          onChange={() => setIssueType('reconsideration')}
        />
        <Form.Check
          inline
          name="issueType"
          type="radio"
          label={
            <>
              {disputeLabel}
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip data-testid="about-tooltip" id="dispute tooltip">
                    Requests to change a reconsideration decision, an initial
                    utilization review decision, or an initial claim decision
                    based on medical necessity or experimental/investigational
                    coverage criteria.
                  </Tooltip>
                }
              >
                <HelpIcon
                  data-testid="about-tooltip-trigger"
                  className="icon icon-question-mark fs-12"
                />
              </OverlayTrigger>
            </>
          }
          checked={issueType === 'dispute'}
          onChange={() => setIssueType('dispute')}
        />
        <Form.Check
          inline
          name="issueType"
          type="radio"
          label={generalInquiryLabel}
          checked={issueType === 'generalInquiry'}
          onChange={() => setIssueType('generalInquiry')}
        />
      </Row>
      {issueType === 'dispute' && (
        <Alert variant="warning">
          <h5>Have you submitted a reconsideration yet?</h5>
          Please submit a reconsideration prior to submitting a dispute/appeal
          if it has not been done yet.
        </Alert>
      )}
      {issueReasonOptions.length > 0 && (
        <Row>
          <Col sm={6} xs={12}>
            <Form.Group>
              <FormLabel>Select reason for {reasonSelectLabel}</FormLabel>
              <Form.Control
                as="select"
                className="bg-white"
                onChange={(e) => setIssueReason(e.target.value)}
                value={issueReason}
              >
                {issueReasonOptions.map((opt) => (
                  <option value={opt}>{opt}</option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
      )}
      <Row style={{ paddingRight: 15, paddingLeft: 15, marginBottom: 15 }}>
        <FormLabel>Please provide context for your selection</FormLabel>
        <Form.Control
          as="textarea"
          className="bg-white w-100"
          required
          onChange={(e: any) => setIssueContext(e.target.value)}
          value={issueContext}
        />
      </Row>
      <Row style={{ marginBottom: 15 }}>
        <Col>
          <FormLabel>Upload Documents/Files</FormLabel>
          <FileDropzone
            key={fileDropzoneKey}
            onFileLoaded={(e: File[]) => {
              setFilesToUpload(e)
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>How would you like to receive our response?</FormLabel>
            <Form.Control
              as="select"
              className="bg-white"
              onChange={(e: any) => setResponseType(e.target.value)}
              value={responseType}
            >
              <option value="email">Email</option>
              <option value="fax">Fax number</option>
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col sm={6} xs={12}>
          <Form.Group>
            <FormLabel>Requestor name</FormLabel>
            <FormControl
              required
              onChange={(e: any) => setRequestorName(e.target.value)}
              value={requestorName}
              placeholder="Please enter a requestor name"
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col sm={6} xs={12}>
          {responseType === 'email' ? (
            <Form.Group>
              <FormLabel>Email</FormLabel>
              <FormControl
                required
                onChange={(e: any) => setEmail(e.target.value)}
                value={email}
                placeholder="Please enter an email address"
              />
            </Form.Group>
          ) : (
            <Form.Group>
              <FormLabel>Fax number</FormLabel>
              <PhoneControl
                required
                key={faxKey}
                onChange={(value: string) => setFax(value)}
                placeholder="Please enter a fax number"
              />
            </Form.Group>
          )}
        </Col>
        <Col sm={6} xs={12}>
          {' '}
          <Form.Group>
            <FormLabel>Phone (optional)</FormLabel>
            <PhoneControl
              key={phoneKey}
              onChange={(value: string) => setPhone(value)}
              placeholder="Please enter a phone number"
            />
          </Form.Group>
        </Col>
      </Row>
      {createTicket.isLoading ? (
        <CenteredSpinner />
      ) : (
        <Button className="my-3 py-2" type="submit">
          Submit
        </Button>
      )}
      {createTicket.isError && (
        <Col
          className="mt-5 py-3 px-4 fs-20"
          sm={6}
          xs={12}
          style={{ background: 'rgba(237, 76, 120, 0.1)', borderRadius: 6 }}
        >
          An unexpected error occurred creating your ticket. Please reach out to
          our Care Team by phone at +1 (855) 937-1811.
        </Col>
      )}
    </Form>
  )
})
